import { z } from 'zod';
import { zAddress, zObjectId } from './primitives.js';
/**
 * Copy-trade settings (the "system settings" panel). Amounts a user types are SOL (numbers);
 * the engine converts to lamports. Percentages are 0–100. `null` means "no limit".
 *
 * The Rust engine mirrors this shape (`ultrasnip-core::settings`); a shared JSON fixture keeps
 * both sides' defaults identical.
 */
const sol = (max = 1_000) => z.number().min(0).max(max);
const pct = (max = 100) => z.number().min(0).max(max);
const optional = (schema) => schema.nullable();
export const SizingMode = { Fixed: 'fixed', Percent: 'percent', Supply: 'supply' };
export const zSizing = z.object({
    mode: z.enum([SizingMode.Fixed, SizingMode.Percent, SizingMode.Supply]),
    /** Fixed size per buy (SOL). */
    fixedSol: sol(100).min(0.001),
    /** Share of the leader's buy (%). */
    percent: pct(1_000).min(0.1),
    /** Share of the coin's total supply to buy (%); the SOL is priced from live reserves. */
    supplyPct: pct(20).min(0.001),
    /** Per-buy range (SOL) for `percent` and `supply` sizing. */
    minSol: sol(100),
    maxSol: sol(100).min(0.001),
});
/** A market-cap band with its own sizing; the first band containing the coin's market cap wins. */
export const zSizingRule = z.object({
    minMarketCapUsd: optional(sol(1e12)),
    maxMarketCapUsd: optional(sol(1e12)),
    sizing: zSizing,
});
export const PriorityFeeMode = { Fixed: 'fixed', Dynamic: 'dynamic' };
/** Token age bounds are in seconds; 30 days is the largest that means anything for memecoins. */
const ageSec = () => z
    .number()
    .int()
    .min(0)
    .max(60 * 60 * 24 * 30);
export const zTakeProfitTier = z.object({
    /** Unrealized gain (%) that triggers this tier. */
    gainPct: pct(100_000).min(1),
    /** Share of the remaining position to sell (%). */
    sellPct: pct().min(1),
});
export const zStrategySettings = z.object({
    buy: z.object({
        /** Copy the leader's buys. */
        enabled: z.boolean(),
        sizing: zSizing,
        /** Most SOL held in one token across buys ("max position"). */
        maxPositionSol: sol(1_000).min(0.001),
        /** How many times we buy the same token (1 = buy once). */
        maxBuysPerToken: z.number().int().min(1).max(50),
        /** Only copy the leader's first buy of a token. */
        onlyFirstBuy: z.boolean(),
        /** Ignore leader buys smaller / larger than this (SOL). */
        minLeaderBuySol: sol(1_000),
        maxLeaderBuySol: optional(sol(100_000)),
        /** Skip when our price would be this much worse than the leader's fill (%). */
        maxPriceDeviationPct: pct(1_000),
        /** Skip signals older than this when they reach the engine (ms). */
        maxSignalAgeMs: z.number().int().min(100).max(60_000),
        slippagePct: pct(99).min(0.1),
        /** Total SOL spent on copy buys per UTC day, across all of the user's strategies (paper and
         * live counted separately). */
        dailySpendCapSol: optional(sol(100_000)),
        /** Market-cap bands with their own sizing (empty = off); outside every band `sizing` applies. */
        sizingRules: z.array(zSizingRule).max(8),
        /** Skip when our own buy would move the price more than this (%), from live reserves. */
        maxPriceImpactPct: optional(pct(100).min(0.1)),
    }),
    sell: z.object({
        /** Mirror the leader's sells ("sell when they sell"). */
        enabled: z.boolean(),
        /** Sell the same share the leader sold, or everything on any leader sell. */
        mode: z.enum(['proportional', 'full']),
        /** Ignore leader sells smaller than this share of their position (%). */
        minLeaderSellPct: pct(),
        slippagePct: pct(99).min(0.1),
    }),
    filters: z.object({
        minMarketCapUsd: optional(sol(1e12)),
        maxMarketCapUsd: optional(sol(1e12)),
        /** pump.fun bonding-curve progress bounds (%). */
        minCurveProgressPct: optional(pct()),
        maxCurveProgressPct: optional(pct()),
        venues: z.object({ pumpCurve: z.boolean(), pumpSwap: z.boolean(), other: z.boolean() }),
        /** Copy mayhem-mode coins (agent-driven pricing). */
        allowMayhem: z.boolean(),
        /** Never buy these mints. */
        blacklist: z.array(zAddress).max(500),
        /** Minimum SOL liquidity in the curve or pool, valued in USD. */
        minLiquidityUsd: optional(sol(1e12)),
        /** Token age bounds (seconds since the mint was created). */
        minTokenAgeSec: optional(ageSec()),
        maxTokenAgeSec: optional(ageSec()),
        /** Simulate a buy and an immediate sell before buying; skip tokens that can't be sold. */
        testSell: z.boolean(),
    }),
    exits: z.object({
        takeProfit: z.array(zTakeProfitTier).max(8),
        /** At this gain, sell exactly enough to recover what was invested. */
        initialsOut: z.object({ enabled: z.boolean(), atGainPct: pct(100_000).min(1) }),
        stopLoss: z.object({ enabled: z.boolean(), lossPct: pct(99).min(1) }),
        trailingStop: z.object({
            enabled: z.boolean(),
            /** Arms once the gain reaches this (%). */
            activateAtGainPct: pct(100_000),
            /** Sells when value falls this far from its peak (%). */
            trailPct: pct(99).min(1),
        }),
        maxHoldMinutes: optional(z
            .number()
            .int()
            .min(1)
            .max(60 * 24 * 30)),
        /** Slippage for stop-loss and panic sells (%). */
        emergencySlippagePct: pct(99).min(0.1),
        /** Move bought tokens to a selling wallet after the buy confirms (walletId null = off). */
        sellWallet: z.object({
            walletId: zObjectId.nullable(),
            /** SOL the selling wallet keeps for fees; below it, tokens stay where they were bought. */
            minFeeSol: sol(1),
        }),
    }),
    execution: z.object({
        /** Total priority fee per transaction (SOL). */
        priorityFeeSol: sol(0.1),
        /** Tip to the landing service per transaction (SOL; Helius Sender needs ≥ 0.0002). */
        tipSol: sol(0.1),
        /** Fixed: always `priorityFeeSol`. Dynamic: from recent network fees, never below
         * `priorityFeeSol` nor above `maxPriorityFeeSol`. */
        priorityFeeMode: z.enum([PriorityFeeMode.Fixed, PriorityFeeMode.Dynamic]),
        maxPriorityFeeSol: sol(0.1),
        /** Retry a trade that definitely failed (never one whose outcome is unknown). */
        retry: z.object({
            /** Extra attempts after the first (0 = off). */
            attempts: z.number().int().min(0).max(5),
            /** Give up once the original signal is this old (ms). */
            maxAgeMs: z.number().int().min(500).max(60_000),
            /** Slippage added per retry (percentage points), capped by the emergency slippage. */
            slippageStepPct: pct(50),
        }),
    }),
    risk: z.object({
        /** Never spend below this wallet balance (SOL). */
        reserveSol: sol(1_000),
        maxOpenPositions: z.number().int().min(1).max(500),
        /** Pause buys after losing this much in a UTC day (SOL, realized). */
        dailyLossLimitSol: optional(sol(100_000)),
        /** Pause buys after this many failed trades in a row. */
        maxConsecutiveFailures: z.number().int().min(1).max(100),
    }),
});
/** Sensible defaults for unattended copy trading (see docs/SETTINGS.md). */
export const DEFAULT_STRATEGY_SETTINGS = {
    buy: {
        enabled: true,
        sizing: {
            mode: SizingMode.Fixed,
            fixedSol: 0.05,
            percent: 10,
            supplyPct: 0.1,
            minSol: 0.01,
            maxSol: 0.5,
        },
        maxPositionSol: 1,
        maxBuysPerToken: 1,
        onlyFirstBuy: false,
        minLeaderBuySol: 0.05,
        maxLeaderBuySol: null,
        maxPriceDeviationPct: 20,
        maxSignalAgeMs: 3_000,
        slippagePct: 15,
        dailySpendCapSol: null,
        sizingRules: [],
        maxPriceImpactPct: null,
    },
    sell: { enabled: true, mode: 'proportional', minLeaderSellPct: 10, slippagePct: 20 },
    filters: {
        minMarketCapUsd: null,
        maxMarketCapUsd: null,
        minCurveProgressPct: null,
        maxCurveProgressPct: null,
        venues: { pumpCurve: true, pumpSwap: true, other: false },
        allowMayhem: false,
        blacklist: [],
        minLiquidityUsd: null,
        minTokenAgeSec: null,
        maxTokenAgeSec: null,
        testSell: false,
    },
    exits: {
        takeProfit: [],
        initialsOut: { enabled: false, atGainPct: 100 },
        stopLoss: { enabled: true, lossPct: 40 },
        trailingStop: { enabled: false, activateAtGainPct: 50, trailPct: 25 },
        maxHoldMinutes: null,
        emergencySlippagePct: 30,
        sellWallet: { walletId: null, minFeeSol: 0.01 },
    },
    execution: {
        priorityFeeSol: 0.0005,
        tipSol: 0.0002,
        priorityFeeMode: PriorityFeeMode.Fixed,
        maxPriorityFeeSol: 0.005,
        retry: { attempts: 0, maxAgeMs: 5_000, slippageStepPct: 5 },
    },
    risk: { reserveSol: 0.02, maxOpenPositions: 10, dailyLossLimitSol: null, maxConsecutiveFailures: 5 },
};
export const zStrategyName = z.string().trim().min(1, 'Name the strategy').max(40);
export const zCreateStrategy = z.object({
    name: zStrategyName,
    /** Copy settings from this strategy (defaults when omitted). */
    fromStrategyId: zObjectId.optional(),
});
export const zUpdateStrategy = z.object({
    name: zStrategyName.optional(),
    walletId: zObjectId.nullable().optional(),
    settings: zStrategySettings.optional(),
});
/** Cross-field rules the schema alone can't express. Returns problems keyed by field path. */
export function validateStrategySettings(settings) {
    const problems = {};
    const { buy, filters, exits } = settings;
    if (buy.sizing.minSol > buy.sizing.maxSol)
        problems['buy.sizing.minSol'] = 'Minimum is above maximum';
    if (buy.maxLeaderBuySol !== null && buy.minLeaderBuySol > buy.maxLeaderBuySol) {
        problems['buy.minLeaderBuySol'] = 'Minimum is above maximum';
    }
    if (filters.minMarketCapUsd !== null &&
        filters.maxMarketCapUsd !== null &&
        filters.minMarketCapUsd > filters.maxMarketCapUsd) {
        problems['filters.minMarketCapUsd'] = 'Minimum is above maximum';
    }
    if (filters.minCurveProgressPct !== null &&
        filters.maxCurveProgressPct !== null &&
        filters.minCurveProgressPct > filters.maxCurveProgressPct) {
        problems['filters.minCurveProgressPct'] = 'Minimum is above maximum';
    }
    const gains = exits.takeProfit.map((t) => t.gainPct);
    if (new Set(gains).size !== gains.length)
        problems['exits.takeProfit'] = 'Two tiers use the same gain';
    if (!Object.values(filters.venues).some(Boolean))
        problems['filters.venues'] = 'Allow at least one venue';
    if (filters.minTokenAgeSec !== null &&
        filters.maxTokenAgeSec !== null &&
        filters.minTokenAgeSec > filters.maxTokenAgeSec) {
        problems['filters.minTokenAgeSec'] = 'Minimum is above maximum';
    }
    buy.sizingRules.forEach((rule, index) => {
        if (rule.minMarketCapUsd !== null &&
            rule.maxMarketCapUsd !== null &&
            rule.minMarketCapUsd > rule.maxMarketCapUsd) {
            problems[`buy.sizingRules.${index}.minMarketCapUsd`] = 'Minimum is above maximum';
        }
        if (rule.minMarketCapUsd === null && rule.maxMarketCapUsd === null) {
            problems[`buy.sizingRules.${index}.minMarketCapUsd`] = 'Set a minimum or a maximum market cap';
        }
        if (rule.sizing.minSol > rule.sizing.maxSol) {
            problems[`buy.sizingRules.${index}.sizing.minSol`] = 'Minimum is above maximum';
        }
    });
    const { execution } = settings;
    if (execution.priorityFeeMode === 'dynamic' && execution.maxPriorityFeeSol < execution.priorityFeeSol) {
        problems['execution.maxPriorityFeeSol'] = 'The cap is below the minimum priority fee';
    }
    return problems;
}
// ── Per-tracked-wallet overrides (layer 3) ──────────────────────────────────────────────────────
// Resolution: built-in defaults → the wallet's strategy → the wallet's overrides. The engine mirrors
// `resolveSettings` in `ultrasnip-core::settings::StrategySettings::merged`.
const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
/** Derived from the schema itself, so a setting added to `zStrategySettings` can be overridden too. */
function deepPartialSchema(schema) {
    if (!(schema instanceof z.ZodObject))
        return schema;
    const shape = {};
    for (const [key, field] of Object.entries(schema.shape)) {
        shape[key] = deepPartialSchema(field).optional();
    }
    // Unknown keys are refused: a typo must not be stored as a silent no-op.
    return z.strictObject(shape);
}
/** Validates the shape and ranges of overrides (not their combination with a strategy). */
export const zStrategyOverrides = deepPartialSchema(zStrategySettings);
function mergeDeep(base, overlay) {
    if (overlay === undefined)
        return base;
    if (!isPlainObject(base) || !isPlainObject(overlay))
        return overlay;
    const merged = { ...base };
    for (const [key, value] of Object.entries(overlay)) {
        if (value !== undefined)
            merged[key] = mergeDeep(merged[key], value);
    }
    return merged;
}
/** `base` with `overrides` on top: objects merge key by key; arrays, `null` and values replace. */
export function resolveSettings(base, overrides) {
    return mergeDeep(base, overrides ?? {});
}
function fillDefaults(defaults, stored) {
    if (!isPlainObject(defaults))
        return stored === undefined ? defaults : stored;
    const source = isPlainObject(stored) ? stored : {};
    return Object.fromEntries(Object.entries(defaults).map(([key, value]) => [key, fillDefaults(value, source[key])]));
}
/**
 * Stored settings made complete: a field a newer version added takes its default, fields the schema
 * no longer has are dropped (as the engine's `StrategySettings::from_stored` does).
 */
export function withStrategyDefaults(stored) {
    return fillDefaults(DEFAULT_STRATEGY_SETTINGS, stored);
}
/** The dotted paths an overrides object sets ("buy.sizing.fixedSol"); arrays count as one. */
export function overridePaths(overrides, prefix = '') {
    if (!isPlainObject(overrides))
        return [];
    return Object.entries(overrides).flatMap(([key, value]) => {
        const path = prefix ? `${prefix}.${key}` : key;
        if (value === undefined)
            return [];
        return isPlainObject(value) ? overridePaths(value, path) : [path];
    });
}
/** Drops `undefined` values and empty objects, so "no overrides" is always `{}`. */
export function pruneOverrides(overrides) {
    const prune = (node) => {
        const out = {};
        for (const [key, value] of Object.entries(node)) {
            if (value === undefined)
                continue;
            if (isPlainObject(value)) {
                const child = prune(value);
                if (Object.keys(child).length > 0)
                    out[key] = child;
            }
            else {
                out[key] = value;
            }
        }
        return out;
    };
    return isPlainObject(overrides) ? prune(overrides) : {};
}
/**
 * Everything wrong with a complete settings object, keyed by field path: schema problems first (one
 * per path), then the cross-field rules. Empty when the engine can use it.
 */
export function strategySettingsProblems(settings) {
    const parsed = zStrategySettings.safeParse(settings);
    if (!parsed.success) {
        const problems = {};
        for (const issue of parsed.error.issues)
            problems[issue.path.join('.')] ??= issue.message;
        return problems;
    }
    return validateStrategySettings(parsed.data);
}
//# sourceMappingURL=strategies.js.map